import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useScrollToTop from '@/hooks/useScrollToTop';

const testimonials = [
  {
    name: '王小明',
    role: '资深韩漫读者',
    avatar: 'https://readdy.ai/api/search-image?query=Cute%20minimalist%20avatar%20illustration%2C%20young%20Asian%20person%2C%20warm%20friendly%20expression%2C%20simple%20flat%20design%2C%20soft%20colors%2C%20circular%20portrait%2C%20clean%20background&width=80&height=80&seq=avatar-01&orientation=squarish',
    content: '韩漫之家的阅读体验真的太棒了！下拉式阅读非常流畅，而且完全免费。我已经在这里追了十几部韩漫了，更新速度也很快，强烈推荐给所有韩漫爱好者！',
    rating: 5,
  },
  {
    name: '李婷婷',
    role: '漫画爱好者',
    avatar: 'https://readdy.ai/api/search-image?query=Cute%20minimalist%20avatar%20illustration%2C%20young%20Asian%20woman%2C%20friendly%20smile%2C%20simple%20flat%20design%2C%20soft%20warm%20colors%2C%20circular%20portrait%2C%20clean%20background&width=80&height=80&seq=avatar-02&orientation=squarish',
    content: '分类很齐全，恋爱类和奇幻类的韩漫都特别好看。手机阅读适配得也很好，每天通勤路上必看！感谢韩漫之家提供这么好的平台。',
    rating: 5,
  },
  {
    name: '张伟',
    role: '二次元文化博主',
    avatar: 'https://readdy.ai/api/search-image?query=Cute%20minimalist%20avatar%20illustration%2C%20young%20Asian%20man%2C%20cool%20expression%2C%20simple%20flat%20design%2C%20soft%20colors%2C%20circular%20portrait%2C%20clean%20background&width=80&height=80&seq=avatar-03&orientation=squarish',
    content: '作为一个二次元文化博主，我经常向粉丝推荐韩漫之家。这里的漫画质量很高，而且分类清晰，新手也能快速找到自己喜欢的类型。界面设计简洁大方，体验一级棒！',
    rating: 5,
  },
];

export default function AboutPage() {
  useScrollToTop();

  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }
    if (contactForm.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }
    try {
      const formData = new FormData(form);
      const resp = await fetch('https://readdy.ai/api/form/d9pcu52qs38sj6jc7lvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setContactForm({ name: '', email: '', message: '' });
          return;
        }
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫之家',
    url: 'https://www.manhuazhijia.com/',
    description: '免费韩漫在线观看平台，提供下拉式韩漫大全',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@manhuazhijia.com',
      contactType: 'customer service',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫之家',
    description: '韩漫之家品牌介绍、口碑声誉和联系方式',
    url: 'https://www.manhuazhijia.com/about',
  };

  return (
    <>
      <SEOHead
        title="关于我们_韩漫之家品牌介绍_联系方式"
        description="韩漫之家是领先的免费韩漫在线观看平台，提供下拉式韩漫大全。了解我们的品牌故事、用户口碑，以及如何联系我们获取帮助。"
        keywords="韩漫之家,关于我们,免费韩漫,韩漫平台,联系我们"
        canonicalUrl="https://www.manhuazhijia.com/about"
        schemaData={[orgSchema, aboutSchema]}
      />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于韩漫之家
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
              韩漫之家致力于为广大韩漫爱好者提供优质的免费韩漫在线观看服务，让每一位读者都能轻松畅享下拉式韩漫的阅读乐趣。
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-book-open-line', title: '海量资源', desc: '收录数千部优质韩漫作品，涵盖恋爱、奇幻、动作等多种类型' },
                { icon: 'ri-smartphone-line', title: '流畅阅读', desc: '下拉式阅读体验，完美适配手机和电脑，随时随地畅读无阻' },
                { icon: 'ri-shield-check-line', title: '正版内容', desc: '与韩国漫画平台合作，提供正版授权的韩漫内容，保护创作者权益' },
              ].map((item) => (
                <div key={item.title} className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 mb-4">
                    <i className={`${item.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">我们的故事</h2>
              <p className="text-foreground-500 text-sm mt-3 max-w-lg mx-auto">
                从一个小小的梦想开始，成为数百万韩漫爱好者的阅读家园
              </p>
            </div>
            <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/70">
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                韩漫之家成立于2024年，由一群热爱韩漫的年轻人共同创立。我们发现国内韩漫爱好者缺乏一个优质的免费阅读平台，于是决定打造韩漫之家 — 一个专注于韩漫的在线观看平台。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                我们与多家韩国漫画平台和出版社建立了合作关系，确保平台上所有内容均为正版授权。同时，我们不断优化阅读体验，引入下拉式阅读模式，让手机阅读变得前所未有的流畅。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                如今，韩漫之家已拥有超过50万活跃读者，收录数千部优质韩漫作品。我们始终秉持"让每一个人都能免费享受韩漫的乐趣"的初心，持续为读者带来更好的服务。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">读者评价</h2>
              <p className="text-foreground-500 text-sm mt-3">来自真实读者的口碑推荐</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.rating }, (_, ri) => (
                      <div key={ri} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">联系我们</h2>
                <p className="text-sm text-foreground-500 mb-6">
                  有任何问题或建议？欢迎通过以下方式联系我们，我们会尽快回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">contact@manhuazhijia.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-customer-service-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">在线客服</p>
                      <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">发送留言</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="visually-hidden"
                  />
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="电子邮箱"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="请描述您的问题或建议（500字以内）"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}